import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../supabase'
import Median from 'median-js-bridge'
import { shareChurchInvite } from '../lib/shareInvite'

// True inside the Median native app (UA token is synchronous on first paint).
function inMedianApp() {
  if (typeof navigator !== 'undefined' && /median|gonative/i.test(navigator.userAgent || '')) return true
  if (typeof window !== 'undefined' && window.median) return true
  try { return !!(Median.isNativeApp && Median.isNativeApp()) } catch { return false }
}

// Open a URL in the device's system browser via the Median bridge (Google
// allows OAuth there; it only blocks embedded WebViews).
function openInSystemBrowser(url) {
  const m = typeof window !== 'undefined' ? window.median : null
  if (m && typeof m.openExternalUrl === 'function') { m.openExternalUrl(url); return }
  try { Median.window.open(url, 'external'); return } catch (_e) { /* fall through */ }
  if (typeof window !== 'undefined') window.location.href = url
}

const NAVY   = 'var(--ink)'
const ACCENT = 'var(--gold)'

const s = {
  wrap:    { minHeight:'100vh', background:'var(--cream)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'24px', fontFamily:"'Plus Jakarta Sans',sans-serif" },
  card:    { background:'var(--white)', border:'1px solid var(--border-strong)', borderRadius:20, padding:'32px 28px', width:'100%', maxWidth:400 },
  logo:    { fontSize:12, fontWeight:800, letterSpacing:'0.2em', textTransform:'uppercase', color:'var(--gold-ink)', marginBottom:6 },
  appName: { fontSize:22, fontWeight:800, color:'var(--ink)', marginBottom:4 },
  sub:     { fontSize:14, color:'var(--stone)', marginBottom:24, lineHeight:1.5 },
  label:   { fontSize:11, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--stone)', marginBottom:6, display:'block', marginTop:14 },
  input:   { width:'100%', background:'var(--white)', border:'1px solid var(--border-strong)', borderRadius:10, padding:'12px 14px', color:'var(--ink)', fontSize:15, fontFamily:"'Plus Jakarta Sans',sans-serif", boxSizing:'border-box', outline:'none' },
  btn:     { width:'100%', background:ACCENT, color:NAVY, border:'none', borderRadius:10, padding:'14px', fontSize:15, fontWeight:800, cursor:'pointer', marginTop:16, fontFamily:"'Plus Jakarta Sans',sans-serif" },
  btnDis:  { opacity:0.4, cursor:'default' },
  msg:     { background:'rgba(232,200,122,0.1)', border:'1px solid rgba(232,200,122,0.25)', borderRadius:10, padding:'12px 14px', fontSize:13, color:'var(--gold-ink)', marginTop:16, lineHeight:1.5 },
  err:     { background:'rgba(200,55,42,0.1)', border:'1px solid rgba(200,55,42,0.25)', borderRadius:10, padding:'12px 14px', fontSize:13, color:'#C8372A', marginTop:16 },
  divider: { textAlign:'center', color:'var(--stone-dim)', fontSize:12, margin:'20px 0' },
  roleRow: { display:'flex', gap:8, marginBottom:12 },
  roleBt:  { flex:1, background:'var(--white)', border:'1px solid var(--border-strong)', borderRadius:8, padding:'10px', fontSize:13, fontWeight:600, color:'var(--stone)', cursor:'pointer', textAlign:'center' },
  roleOn:  { background:'rgba(232,200,122,0.12)', borderColor:'rgba(232,200,122,0.4)', color:'var(--gold-ink)' },
}

// ── STANDARD LOGIN / VISITOR SIGNUP - combined screen with a toggle.
// "Sign in" is the existing email-only flow for members who already have an
// account (church + role come from their existing profile).
// "Sign up" is the new visitor flow - anyone can sign up with their email
// and optionally pick a church. They get role='visitor' on first sign-in.
// Standard multi-color Google "G" mark. Inline SVG so we don't pull a deps.
function GoogleLogo({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.12-.84 2.07-1.79 2.71v2.26h2.9c1.7-1.57 2.69-3.88 2.69-6.61z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.19l-2.9-2.26c-.81.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.96v2.34A8.99 8.99 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.95 10.69A5.4 5.4 0 0 1 3.66 9c0-.59.1-1.16.29-1.69V4.97H.96A8.99 8.99 0 0 0 0 9c0 1.45.35 2.83.96 4.03l2.99-2.34z"/>
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A8.99 8.99 0 0 0 .96 4.97l2.99 2.34C4.66 5.18 6.65 3.58 9 3.58z"/>
    </svg>
  )
}

function AppleLogo() {
  return (
    <svg width="15" height="18" viewBox="0 0 384 512" fill="white" aria-hidden="true" style={{display:'block'}}>
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
    </svg>
  )
}

// Apple sign-in is only offered on iOS / inside the Median wrapper (Apple
// requires the button there; on Android/desktop web we hide it).
function appleSignInAvailable() {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  const isMedian = typeof window.median !== 'undefined'
  return isIOS || isMedian
}

// Six individual digit boxes with auto-advance, backspace-to-previous,
// full-code paste support, and auto-submit on completion. Gold border on
// the focused box matches the rest of the app accent. `autoComplete=
// "one-time-code"` on the first input (and `form autoComplete=…`) lets iOS
// + Android offer the code from the verification email via QuickType.
export function OtpCodeInputs({ value, onChange, onSubmit, autoFocus, disabled, length = 6 }) {
  const inputs = useRef([])
  const [focusedIdx, setFocusedIdx] = useState(autoFocus ? 0 : -1)
  const digits = (value || '').padEnd(length, ' ').slice(0, length).split('').map(c => c === ' ' ? '' : c)
  // Boxes shrink slightly as length grows so 8 still fits in the card.
  const boxW = length >= 8 ? 38 : 48
  const gap  = length >= 8 ? 6  : 8
  const font = length >= 8 ? 20 : 24

  function setDigit(i, raw) {
    const cleanedAll = String(raw || '').replace(/\D/g, '')
    // Multiple digits landed in one box — the OS one-time-code autofill (or a
    // paste) dumping the whole code. Distribute them across the boxes from here
    // rather than keeping only the first digit. Single-digit typing falls
    // through to the normal path below unchanged.
    if (cleanedAll.length > 1) {
      const next = digits.slice()
      for (let k = 0; k < cleanedAll.length && i + k < length; k++) next[i + k] = cleanedAll[k]
      const combined = next.join('').replace(/\s/g, '')
      onChange(combined)
      inputs.current[Math.min(i + cleanedAll.length, length - 1)]?.focus()
      if (combined.length === length) onSubmit?.(combined)
      return
    }
    const cleaned = cleanedAll.slice(0, 1)
    const next = digits.slice()
    next[i] = cleaned
    const combined = next.join('').replace(/\s/g, '')
    onChange(combined)
    if (cleaned && i < length - 1) inputs.current[i + 1]?.focus()
    if (combined.length === length) onSubmit?.(combined)
  }

  function onKey(i, e) {
    if (e.key === 'Backspace') {
      if (!digits[i] && i > 0) {
        // Empty box — jump back AND clear the previous one (spec).
        const next = digits.slice()
        next[i - 1] = ''
        onChange(next.join('').replace(/\s/g, ''))
        inputs.current[i - 1]?.focus()
        e.preventDefault()
      }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      e.preventDefault(); inputs.current[i - 1]?.focus()
    } else if (e.key === 'ArrowRight' && i < length - 1) {
      e.preventDefault(); inputs.current[i + 1]?.focus()
    }
  }

  function onPaste(e) {
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, length)
    if (!pasted) return
    e.preventDefault()
    onChange(pasted)
    const focusIdx = Math.min(pasted.length, length - 1)
    inputs.current[focusIdx]?.focus()
    if (pasted.length === length) onSubmit?.(pasted)
  }

  // iOS / Android one-time-code autofill works best when a SINGLE
  // input carries `autocomplete="one-time-code"`. With it spread across
  // multiple boxes the system loses confidence and silently skips the
  // QuickType suggestion. So we render one hidden full-length input as
  // the autofill target and keep the visible boxes purely display.
  // When the hidden input changes (real typing OR autofill), we
  // distribute the digits to the boxes via the same onChange callback
  // and auto-submit if all positions are filled.
  function onHiddenInputChange(e) {
    const cleaned = String(e.target.value || '').replace(/\D/g, '').slice(0, length)
    onChange(cleaned)
    if (cleaned.length === length) onSubmit?.(cleaned)
  }

  return (
    <div style={{position:'relative', marginTop:8}}>
      {/* Hidden autofill target — the only input iOS / Android sees as
          the one-time-code field. Visually clipped to 1×1 so the system
          accepts it as a real field but the user never sees it. */}
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        name="one-time-code"
        aria-label="One-time code"
        maxLength={length}
        disabled={disabled}
        value={(value || '').slice(0, length)}
        onChange={onHiddenInputChange}
        tabIndex={-1}
        style={{
          position:'absolute', top:0, left:'50%', transform:'translateX(-50%)',
          width:1, height:1, padding:0, border:0,
          opacity:0, pointerEvents:'none',
          // Some browsers refuse autofill on display:none / visibility:hidden
          // inputs, so we keep it in the layout but visually empty.
          background:'transparent', color:'transparent', caretColor:'transparent',
        }}/>

      <div style={{display:'flex', gap, justifyContent:'center'}}>
        {Array.from({length}).map((_, i) => {
          const isFocused = focusedIdx === i
          return (
            <input key={i}
              ref={el => (inputs.current[i] = el)}
              // type="text" not "number" — iOS one-time-code auto-fill is
              // more reliable with text + inputMode="numeric" than with
              // type="number".
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              // Visible boxes never carry the one-time-code flag — the
              // hidden input above owns autofill. These are purely for
              // typing / display.
              autoComplete="off"
              // Allow the full code so an autofill/paste reaching this box
              // isn't truncated to one digit before setDigit can distribute it.
              // The controlled value still renders only this box's single digit.
              maxLength={length}
              disabled={disabled}
              value={digits[i] || ''}
              onChange={e => setDigit(i, e.target.value)}
              onKeyDown={e => onKey(i, e)}
              onPaste={i === 0 ? onPaste : undefined}
              onFocus={() => setFocusedIdx(i)}
              onBlur={() => setFocusedIdx(idx => (idx === i ? -1 : idx))}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={autoFocus && i === 0}
              style={{
                width: boxW, height: boxW, textAlign:'center',
                fontSize: font, fontWeight:700, color:'var(--ink)',
                background:'var(--white)',
                border:`1px solid ${isFocused ? ACCENT : 'var(--border-strong)'}`,
                borderRadius:8,
                fontFamily:"'Plus Jakarta Sans',sans-serif", boxSizing:'border-box', outline:'none',
                transition:'border-color 120ms',
                opacity: disabled ? 0.5 : 1,
              }}/>
          )
        })}
      </div>
    </div>
  )
}

// Supabase emails a 6-digit OTP — keep this in one place so the boxes,
// the validation guard and the copy stay in sync. Bump to 8 if you ever
// switch back to the longer OTP in the Supabase dashboard.
const OTP_LENGTH = 6

export function LoginScreen() {
  const { signInWithEmail, signUpAsVisitor, verifyEmailOtp, authError } = useAuth()
  const [mode,     setMode]     = useState('signin') // 'signin' | 'signup'
  const [email,    setEmail]    = useState('')
  const [fullName, setFullName] = useState('')
  const [sent,     setSent]     = useState(false)
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState('')
  const [oauthLoading, setOauthLoading] = useState(false)
  // Mirror auth-context errors (e.g. profile setup failing after an OAuth
  // callback) into the on-screen error so they're visible on mobile too.
  useEffect(() => { if (authError) { setError(authError); setOauthLoading(false) } }, [authError])
  // Code-verification state — used after the magic link is sent so the user
  // can sign in inside the PWA without leaving for Safari/Chrome.
  const [code,        setCode]        = useState('')
  const [verifying,   setVerifying]   = useState(false)

  async function handleGoogle() {
    setOauthLoading(true); setError('')
    // Match the magic-link redirect logic so Google OAuth bounces back to
     // wherever the user actually is (localhost on dev, prod domain otherwise).
    const native = inMedianApp()
    // Inside the app, grab the OAuth URL (skipBrowserRedirect) and open it in the
    // system browser — signInWithOAuth's JS redirect bypasses Median's Link
    // Handling and gets blocked by Google's embedded-WebView policy otherwise.
    // Redirect to our https /login marked app_oauth=1; the inline bounce in
    // index.html reopens the app via its custom scheme to finish sign-in.
    let redirectTo = (typeof window !== 'undefined' && window.location.hostname === 'localhost')
      ? 'http://localhost:3000'
      : 'https://thebiblecommunity.co.uk/login'
    if (native) redirectTo += (redirectTo.includes('?') ? '&' : '?') + 'app_oauth=1'
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo, skipBrowserRedirect: native },
    })
    if (error) { setError(error.message); setOauthLoading(false); return }
    if (native) {
      if (data?.url) openInSystemBrowser(data.url)
      else { setError('Could not start Google sign-in. Please try again.'); setOauthLoading(false) }
    }
    // On the web, skipBrowserRedirect is false so Supabase already redirected.
  }

  const showAppleSignIn = appleSignInAvailable()
  async function handleApple() {
    setOauthLoading(true); setError('')
    const redirectTo = (typeof window !== 'undefined' && window.location.hostname === 'localhost')
      ? 'http://localhost:3000'
      : 'https://thebiblecommunity.co.uk/login'
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: { redirectTo, scopes: 'name email' },
    })
    if (error) {
      console.error('Apple sign in error:', error)
      setError(`Sign in with Apple failed: ${error.message || 'please try again.'}`)
      setOauthLoading(false)
    }
  }

  const googleBlock = (
    <>
      {showAppleSignIn && (
        <button type="button"
          onClick={handleApple}
          disabled={oauthLoading}
          style={{
            width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:10,
            background:'#000000', color:'#ffffff', border:'1px solid rgba(255,255,255,0.25)',
            borderRadius:8, padding:'14px 24px', fontSize:15, fontWeight:600,
            cursor: oauthLoading ? 'default' : 'pointer',
            fontFamily:"'Plus Jakarta Sans',sans-serif",
            opacity: oauthLoading ? 0.6 : 1,
            marginBottom:10,
          }}>
          <AppleLogo/>
          Continue with Apple
        </button>
      )}
      <button type="button"
        onClick={handleGoogle}
        disabled={oauthLoading}
        style={{
          width:'100%', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:10,
          background:'#fff', color:'#1f2937', border:'1px solid var(--border)',
          borderRadius:10, padding:'12px', fontSize:14, fontWeight:600,
          cursor: oauthLoading ? 'default' : 'pointer',
          fontFamily:"'Plus Jakarta Sans',sans-serif",
          opacity: oauthLoading ? 0.6 : 1,
        }}>
        <GoogleLogo size={18}/>
        {oauthLoading ? 'Redirecting…' : 'Continue with Google'}
      </button>
      <div style={{
        display:'flex', alignItems:'center', gap:10, margin:'14px 0',
        color:'var(--stone-dim)', fontSize:11, letterSpacing:'0.18em', textTransform:'uppercase',
      }}>
        <div style={{flex:1, height:1, background:'var(--border-strong)'}}/>
        or
        <div style={{flex:1, height:1, background:'var(--border-strong)'}}/>
      </div>
    </>
  )

  function reset() {
    setSent(false); setError(''); setCode('')
  }

  async function handleVerifyCode(submittedCode) {
    const trimmed = String(submittedCode ?? code).trim()
    if (trimmed.length < OTP_LENGTH) return
    setVerifying(true); setError('')
    const { error: vErr } = await verifyEmailOtp(email.trim(), trimmed)
    setVerifying(false)
    if (vErr) {
      setError('Invalid code — please try again')
      // Clear all boxes so the user can retype without backspacing.
      setCode('')
    }
    // On success the auth-state listener picks up the new session and
    // LoginScreen unmounts — no further action needed here.
  }

  const [resending, setResending] = useState(false)
  const [resentNote, setResentNote] = useState('')
  async function handleResend() {
    if (!email.trim()) return
    setResending(true); setResentNote(''); setError('')
    const { error: rErr } = mode === 'signup'
      ? await signUpAsVisitor(email.trim(), fullName.trim() || null)
      : await signInWithEmail(email.trim())
    setResending(false)
    if (rErr) setError(rErr.message || 'Could not resend the code.')
    else setResentNote('New code sent.')
  }

  async function handleSignIn(e) {
    e.preventDefault()
    if (!email.trim()) return
    setLoading(true); setError('')
    const { error } = await signInWithEmail(email.trim())
    setLoading(false)
    if (error) {
      // With shouldCreateUser: false Supabase rejects unknown emails with
      // "Signups not allowed for otp". Translate to a clearer next step.
      if (/signups?\s+not\s+allowed|user\s+not\s+found/i.test(error.message || '')) {
        setError("We can't find an account for that email. Tap “Sign up” above to create one.")
      } else {
        setError(error.message)
      }
    } else {
      setSent(true)
    }
  }

  async function handleSignUp(e) {
    e.preventDefault()
    if (!email.trim() || !fullName.trim()) return
    setLoading(true); setError('')
    const { error } = await signUpAsVisitor(email.trim(), fullName.trim())
    setLoading(false)
    if (error) setError(error.message)
    else setSent(true)
  }

  // ── Tab pills
  const tabRow = (
    <div style={{display:'flex', gap:6, background:'var(--white)', border:'1px solid var(--border-strong)', borderRadius:10, padding:4, marginBottom:18}}>
      {[
        { id:'signin', label:'Sign in' },
        { id:'signup', label:'Sign up' },
      ].map(t => (
        <button key={t.id} type="button"
          onClick={()=>{ setMode(t.id); reset(); }}
          style={{
            flex:1, padding:'8px 10px', borderRadius:8,
            background: mode===t.id ? ACCENT : 'transparent',
            color:      mode===t.id ? NAVY : 'var(--stone)',
            border: 'none', fontSize:13, fontWeight:700, cursor:'pointer',
            fontFamily:"'Plus Jakarta Sans',sans-serif",
          }}>
          {t.label}
        </button>
      ))}
    </div>
  )

  return (
    <div style={s.wrap}>
      <div style={s.card}>
        <div style={s.logo}>The Bible Community</div>
        <div style={{...s.appName, marginBottom:16}}>{mode==='signup' ? 'Welcome' : 'Welcome back'}</div>

        {tabRow}

        {sent ? (
          <>
            <div style={s.msg}>
              Check your email — <strong>{email}</strong>
              {mode==='signup' && (
                <div style={{marginTop:6,fontSize:12,opacity:0.85}}>
                  Once you sign in you'll be a <strong>visitor</strong>.
                  You can choose your church next.
                </div>
              )}
            </div>
            <div style={{...s.label, marginTop:18, marginBottom:10, textAlign:'center', letterSpacing:'0.04em', textTransform:'none', fontSize:13, color:'var(--stone)'}}>
              Enter the {OTP_LENGTH} digit code sent to {email}
            </div>
            {/* Wrapping form carries autoComplete="one-time-code" so iOS
                surfaces the code suggestion at the form level, alongside
                the first input that also carries the attribute. */}
            <form autoComplete="one-time-code"
              onSubmit={e => { e.preventDefault(); if (!verifying) handleVerifyCode(code) }}>
              <OtpCodeInputs
                length={OTP_LENGTH}
                value={code}
                onChange={setCode}
                onSubmit={(full) => { if (!verifying) handleVerifyCode(full) }}
                disabled={verifying}
                autoFocus/>
            </form>
            {verifying && (
              <div style={{textAlign:'center', fontSize:12, color:'var(--stone)', marginTop:12}}>
                Verifying…
              </div>
            )}
            {error && <div style={s.err}>{error}</div>}
            {resentNote && (
              <div style={{...s.msg, marginTop:12, fontSize:12}}>{resentNote}</div>
            )}
            <div style={{textAlign:'center', fontSize:12, color:'var(--stone)', marginTop:16}}>
              Didn’t receive a code?{' '}
              <button type="button" onClick={handleResend} disabled={resending}
                style={{
                  background:'transparent', border:'none', color:'var(--gold-ink)',
                  cursor: resending ? 'default' : 'pointer', padding:0,
                  fontSize:12, fontWeight:700, fontFamily:"'Plus Jakarta Sans',sans-serif",
                  textDecoration:'underline', opacity: resending ? 0.5 : 1,
                }}>
                {resending ? 'Resending…' : 'Resend'}
              </button>
            </div>
            <button type="button" onClick={reset}
              style={{
                width:'100%', marginTop:14, padding:'8px',
                background:'transparent', color:'var(--stone)',
                border:'1px solid var(--border-strong)', borderRadius:8,
                fontSize:12, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif",
              }}>
              Use a different email
            </button>
          </>
        ) : mode === 'signin' ? (
          <form onSubmit={handleSignIn}>
            {googleBlock}
            <label style={s.label}>Email address</label>
            <input style={s.input} type="email" placeholder="you@example.com"
              value={email} onChange={e => setEmail(e.target.value)} autoFocus/>
            {error && <div style={s.err}>{error}</div>}
            <button style={{...s.btn,...(loading||!email.trim()?s.btnDis:{})}}
              disabled={loading||!email.trim()}>
              {loading ? 'Sending…' : 'Send code'}
            </button>
            <div style={s.divider}>New here? Tap “Sign up” above.</div>
          </form>
        ) : (
          <form onSubmit={handleSignUp}>
            {googleBlock}
            <label style={s.label}>Your full name</label>
            <input style={s.input} type="text" placeholder="e.g. Sarah Mitchell"
              value={fullName} onChange={e => setFullName(e.target.value)} autoFocus/>
            <label style={s.label}>Email address</label>
            <input style={s.input} type="email" placeholder="you@example.com"
              value={email} onChange={e => setEmail(e.target.value)}/>
            {error && <div style={s.err}>{error}</div>}
            <button type="submit"
              style={{...s.btn,...(loading||!email.trim()||!fullName.trim()?s.btnDis:{})}}
              disabled={loading||!email.trim()||!fullName.trim()}>
              {loading ? 'Sending…' : 'Sign up'}
            </button>
            <div style={{textAlign:'center', fontSize:11, color:'var(--stone)', marginTop:8, lineHeight:1.5}}>
              You'll choose your church on the next step.
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

// ── POST-AUTH CHURCH PICKER - shown after sign-in when the profile has no
// church attached. Lets the user search the church directory and attach
// themselves as a visitor. Skippable - users can pick a church later from
// their profile.
export function ChurchPicker({ onDone }) {
  const { user, profile, churches, updateProfile } = useAuth()
  const [query,    setQuery]    = useState('')
  const [churchId, setChurchId] = useState('')
  const [saving,   setSaving]   = useState(false)
  const [error,    setError]    = useState('')

  const firstName = (
    profile?.full_name
    || user?.user_metadata?.full_name
    || user?.user_metadata?.name
    || user?.email?.split('@')[0]
    || 'there'
  ).split(/\s+/)[0]

  const filtered = (() => {
    const q = query.trim().toLowerCase()
    if (!q) return []
    return (churches || []).filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.location || '').toLowerCase().includes(q)
    )
  })()

  async function handleSave() {
    if (!churchId) return
    setSaving(true); setError('')
    const updates = { church_id: churchId }
    if (!profile?.role) updates.role = 'visitor'
    const { error } = await updateProfile(updates)
    setSaving(false)
    if (error) { setError(error.message); return }
    onDone?.()
  }

  function handleSkip() {
    try { localStorage.setItem(`nlc.churchPickerSkipped:${user?.id}`, '1') } catch {}
    onDone?.()
  }

  return (
    <div style={s.wrap}>
      <div style={s.card}>
        <div style={s.logo}>The Bible Community</div>
        <div style={s.appName}>Welcome {firstName}!</div>
        <div style={{...s.sub, marginBottom:8}}>Which church are you joining?</div>
        <div style={{fontSize:13, color:'var(--stone)', lineHeight:1.5, marginBottom:18}}>
          You'll be added as a church visitor and you'll be able to view all church content.
          Once you're in you can request to join as a member to join the closed community.
        </div>

        <label style={s.label}>Find your church</label>
        <input
          style={{...s.input, marginBottom: query.trim() ? 8 : 0}}
          type="text"
          placeholder="Type a church name or location"
          value={query}
          onChange={e=>{ setQuery(e.target.value); setChurchId(''); }}
          autoFocus
        />

        {/* Can't find your church → share an invite to a church leader. */}
        <button type="button" onClick={shareChurchInvite}
          style={{
            display:'flex', alignItems:'center', gap:10, width:'100%',
            background:'rgba(232,200,122,0.06)', border:'1px solid rgba(232,200,122,0.22)',
            borderRadius:12, padding:'12px 14px', marginTop:10, marginBottom:4, cursor:'pointer',
            fontFamily:"'Plus Jakarta Sans',sans-serif", textAlign:'left',
          }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--gold-ink)"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{flexShrink:0}} aria-hidden="true">
            <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
          <span style={{fontSize:13, lineHeight:1.45, color:'var(--gold-ink)', fontWeight:600}}>
            Can't find your church? Invite your church leader to set up their Bible Community!
          </span>
        </button>

        {query.trim() && (
          <div style={{
            maxHeight:200, overflowY:'auto',
            background:'var(--white)', border:'1px solid var(--border-strong)',
            borderRadius:10, padding:4,
          }}>
            {filtered.length === 0 ? (
              <div style={{padding:'10px 12px', fontSize:13, color:'var(--stone)'}}>
                No churches match “{query}”.
              </div>
            ) : filtered.map(c => (
              <div key={c.id}
                onClick={()=>setChurchId(c.id)}
                style={{
                  padding:'8px 10px', borderRadius:8, cursor:'pointer',
                  background: churchId===c.id ? 'rgba(232,200,122,0.16)' : 'transparent',
                  border: churchId===c.id ? '1px solid rgba(232,200,122,0.4)' : '1px solid transparent',
                  marginBottom:2,
                }}>
                <div style={{fontSize:14, fontWeight:600, color: churchId===c.id ? 'var(--gold-ink)' : 'var(--ink)'}}>{c.name}</div>
                {c.location && <div style={{fontSize:11, color:'var(--stone)'}}>{c.location}</div>}
              </div>
            ))}
          </div>
        )}

        <div style={{fontSize:11, color:'var(--stone)', marginTop:10, lineHeight:1.5, textAlign:'center'}}>
          This is optional, you can add this in your profile later.
        </div>

        {error && <div style={s.err}>{error}</div>}

        <button type="button" onClick={handleSave}
          style={{...s.btn,...((saving||!churchId)?s.btnDis:{})}}
          disabled={saving||!churchId}>
          {saving ? 'Saving…' : 'Continue'}
        </button>
        <div style={{textAlign:'center', marginTop:14}}>
          <button type="button" onClick={handleSkip}
            style={{
              background:'none', border:'none', cursor:'pointer',
              color:'var(--stone)', fontSize:12.5, fontWeight:500,
              fontFamily:"'Plus Jakarta Sans',sans-serif", padding:6,
            }}>
            I'll do this later
          </button>
        </div>
      </div>
    </div>
  )
}

// ── INVITE ACCEPT - shown when someone arrives via /?invite=TOKEN
// Flow: name → photo → code. We collect the photo before sending the code
// (the invitee already came from email, so we never bounce them out to hunt
// for a link). The photo file is held in memory and uploaded right after the
// code is verified — Supabase storage needs an authenticated session, which
// only exists post-verify. Name + church come from the invitation, so once
// verified we mark onboarding_complete and the invitee lands straight in the
// app, skipping the generic onboarding flow.
export function InviteAcceptScreen({ token }) {
  const { signUpFromInvite, verifyEmailOtp, signInWithEmail, refreshProfile } = useAuth()
  const [step,     setStep]     = useState('name') // 'name' | 'photo' | 'verify'
  const [fullName, setFullName] = useState('')
  const [loading,  setLoading]  = useState(false)  // sending the code
  const [error,    setError]    = useState('')
  // Photo is captured here but not uploaded until after verify (storage needs
  // an auth session). We keep the File plus a local object-URL for preview.
  const [photoFile,    setPhotoFile]    = useState(null)
  const [photoPreview, setPhotoPreview] = useState('')
  const photoInputRef = useRef(null)
  // Code-verification state.
  const [email,     setEmail]     = useState('')
  const [code,      setCode]      = useState('')
  const [verifying, setVerifying] = useState(false)
  const [resending, setResending] = useState(false)
  const [resentNote, setResentNote] = useState('')
  // Apple/Google sign-in (redesign Phase 8b) — same handlers as LoginScreen
  // above, but the invite token is stashed in sessionStorage first so
  // App.js's post-OAuth-redirect effect can apply this invitation's role +
  // church to the profile once the session exists (see applyInviteToExistingUser
  // in useAuth.js). Email+OTP below is unaffected.
  const [oauthLoading, setOauthLoading] = useState(false)
  const showAppleSignIn = appleSignInAvailable()
  function stashInviteToken() {
    try { sessionStorage.setItem('nlc.inviteToken', token) } catch (_e) { /* ignore */ }
  }
  async function handleGoogle() {
    setOauthLoading(true); setError('')
    stashInviteToken()
    const native = inMedianApp()
    let redirectTo = (typeof window !== 'undefined' && window.location.hostname === 'localhost')
      ? 'http://localhost:3000'
      : 'https://thebiblecommunity.co.uk/login'
    if (native) redirectTo += (redirectTo.includes('?') ? '&' : '?') + 'app_oauth=1'
    const { data, error: oErr } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo, skipBrowserRedirect: native },
    })
    if (oErr) { setError(oErr.message); setOauthLoading(false); return }
    if (native) {
      if (data?.url) openInSystemBrowser(data.url)
      else { setError('Could not start Google sign-in. Please try again.'); setOauthLoading(false) }
    }
  }
  async function handleAppleOAuth() {
    setOauthLoading(true); setError('')
    stashInviteToken()
    const redirectTo = (typeof window !== 'undefined' && window.location.hostname === 'localhost')
      ? 'http://localhost:3000'
      : 'https://thebiblecommunity.co.uk/login'
    const { error: oErr } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: { redirectTo, scopes: 'name email' },
    })
    if (oErr) {
      setError(`Sign in with Apple failed: ${oErr.message || 'please try again.'}`)
      setOauthLoading(false)
    }
  }
  // Look up the invitation so we can show the role-specific welcome before
  // the user sees the name form. get_invitation(p_token) is a SECURITY
  // DEFINER RPC — the raw invitations table has no public SELECT policy
  // (see supabase-schema-security-invitations.sql) — so this still works
  // without auth, just via the RPC instead of a direct table read.
  const [invite,   setInvite]   = useState(null)
  useEffect(() => {
    if (!token) return
    let cancelled = false
    supabase
      .rpc('get_invitation', { p_token: token })
      .then(({ data }) => {
        if (cancelled) return
        const row = Array.isArray(data) ? data[0] : data
        if (row) setInvite({
          email: row.email || '',
          role: row.role,
          churchName: row.church_name || 'your church',
        })
      })
    return () => { cancelled = true }
  }, [token])

  // Revoke the previous preview object-URL when it changes or on unmount.
  useEffect(() => () => { if (photoPreview) URL.revokeObjectURL(photoPreview) }, [photoPreview])

  function pickPhoto(file) {
    if (!file) return
    setPhotoFile(file)
    setPhotoPreview(URL.createObjectURL(file))
  }

  function goToPhoto(e) {
    e.preventDefault()
    if (!fullName.trim()) return
    setError('')
    setStep('photo')
  }

  // Send the sign-in code once name + (optional) photo are collected.
  async function sendCode() {
    setLoading(true); setError('')
    const { error, email: inviteEmail } = await signUpFromInvite(token, fullName.trim())
    setLoading(false)
    if (error) { setError(error.message); return }
    setEmail(inviteEmail || invite?.email || '')
    setStep('verify')
  }

  async function handleVerifyCode(submittedCode) {
    const targetEmail = email || invite?.email || ''
    const trimmed = String(submittedCode ?? code).trim()
    if (!targetEmail || trimmed.length < OTP_LENGTH) return
    setVerifying(true); setError('')
    const { data, error: vErr } = await verifyEmailOtp(targetEmail, trimmed)
    if (vErr) {
      setVerifying(false)
      setError('Invalid code — please try again')
      setCode('')
      return
    }
    // Verified — we have a session now. Apply the invite's role/church
    // server-side (accept_invitation runs on auth.uid(), so it only works
    // now that a real session exists — see useAuth.js's signUpFromInvite),
    // upload the photo collected earlier, and mark onboarding_complete so
    // the invitee skips the generic onboarding (their name + church already
    // came from the invite). Keyed by the verified user id so it doesn't
    // depend on the auth context having populated `user` yet. Best-effort:
    // a failed photo upload still lets them into the app, so we never block
    // on it — but the invite acceptance itself is awaited since it's the
    // whole point of this screen.
    const userId = data?.user?.id
    if (token) {
      const { error: acceptErr } = await supabase.rpc('accept_invitation', { p_token: token })
      if (acceptErr) console.warn('[invite] accept_invitation failed:', acceptErr.message || acceptErr)
    }
    try {
      const updates = { onboarding_complete: true }
      if (photoFile && userId) {
        const ext  = photoFile.type === 'image/png' ? 'png' : 'jpg'
        const path = `${userId}.${ext}`
        const { error: upErr } = await supabase.storage
          .from('avatars').upload(path, photoFile, { upsert: true, contentType: photoFile.type || 'image/jpeg' })
        if (!upErr) {
          const { data: pub } = supabase.storage.from('avatars').getPublicUrl(path)
          updates.avatar_url = `${pub.publicUrl}?v=${Date.now()}`
        }
      }
      if (userId) await supabase.from('profiles').update(updates).eq('id', userId)
      await refreshProfile()
    } catch (e) {
      console.warn('[invite] post-verify finalize failed:', e)
    }
    // App.js routes the now fully-set-up user straight into the app.
  }

  async function handleResend() {
    const targetEmail = email || invite?.email || ''
    if (!targetEmail) return
    setResending(true); setResentNote(''); setError('')
    const { error: rErr } = await signInWithEmail(targetEmail)
    setResending(false)
    if (rErr) setError(rErr.message || 'Could not resend the code.')
    else setResentNote('New code sent.')
  }

  const churchNameForHeadline = invite?.churchName || 'your church'

  // "Get / open the app" banner — invited users often tap the link in a mobile
  // browser; the native app is the best experience. Shown on the web (not when
  // already inside the Median app), linking to the right store for their OS.
  const _ua = typeof navigator !== 'undefined' ? (navigator.userAgent || '') : ''
  const _inApp = /median|gonative/i.test(_ua)
  const _isAndroid = /android/i.test(_ua)
  const _isIos = /iphone|ipad|ipod/i.test(_ua)
  const storeUrl = _isAndroid
    ? 'https://play.google.com/store/apps/details?id=uk.co.thbiblecommunity.app'
    : _isIos
      ? 'https://apps.apple.com/gb/app/id6776408991'
      : null
  const showAppBanner = !_inApp && !!storeUrl

  return (
    <div style={s.wrap}>
      <div style={s.card}>
        <div style={s.logo}>The Bible Community</div>
        <div style={s.appName}>You've been invited to join {churchNameForHeadline}</div>

        {showAppBanner && (
          <a href={storeUrl} target="_blank" rel="noopener noreferrer"
            style={{
              display:'flex', alignItems:'center', gap:12, textDecoration:'none',
              background:'rgba(232,200,122,0.08)', border:'1px solid rgba(232,200,122,0.3)',
              borderRadius:12, padding:'11px 14px', margin:'14px 0 4px',
            }}>
            <span style={{fontSize:22, flexShrink:0}}>📲</span>
            <span style={{flex:1, minWidth:0}}>
              <span style={{display:'block', fontSize:13.5, fontWeight:700, color:'var(--ink)', fontFamily:"'Plus Jakarta Sans',sans-serif"}}>Get the best experience in the app</span>
              <span style={{display:'block', fontSize:12, color:'var(--stone)', fontFamily:"'Plus Jakarta Sans',sans-serif"}}>You can finish setting up here, or open the app.</span>
            </span>
            <span style={{flexShrink:0, background:ACCENT, color:'var(--ink)', borderRadius:8, padding:'8px 12px', fontSize:12.5, fontWeight:800, fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
              {_isAndroid ? 'Get app' : 'Get app'}
            </span>
          </a>
        )}

        {step === 'name' && (
          <>
            <div style={{...s.sub, marginTop:4, marginBottom:18}}>
              So {churchNameForHeadline} knows it's you. It's free: your church covers it.
            </div>

            {showAppleSignIn && (
              <button type="button" onClick={handleAppleOAuth} disabled={oauthLoading}
                style={{
                  width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:10,
                  background:'#000000', color:'#ffffff', border:'1px solid rgba(255,255,255,0.25)',
                  borderRadius:8, padding:'14px 24px', fontSize:15, fontWeight:600,
                  cursor: oauthLoading ? 'default' : 'pointer',
                  fontFamily:"'Plus Jakarta Sans',sans-serif",
                  opacity: oauthLoading ? 0.6 : 1, marginBottom:10,
                }}>
                <AppleLogo/> Continue with Apple
              </button>
            )}
            <button type="button" onClick={handleGoogle} disabled={oauthLoading}
              style={{
                width:'100%', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:10,
                background:'#fff', color:'#1f2937', border:'1px solid var(--border)',
                borderRadius:10, padding:'12px', fontSize:14, fontWeight:600,
                cursor: oauthLoading ? 'default' : 'pointer',
                fontFamily:"'Plus Jakarta Sans',sans-serif",
                opacity: oauthLoading ? 0.6 : 1, marginBottom:14,
              }}>
              <GoogleLogo size={18}/> {oauthLoading ? 'Redirecting…' : 'Continue with Google'}
            </button>
            <div style={{
              display:'flex', alignItems:'center', gap:10, margin:'4px 0 14px',
              color:'var(--stone-dim)', fontSize:11, letterSpacing:'0.18em', textTransform:'uppercase',
              fontFamily:"'Plus Jakarta Sans',sans-serif",
            }}>
              <div style={{flex:1, height:1, background:'var(--border-strong)'}}/>
              or continue with email
              <div style={{flex:1, height:1, background:'var(--border-strong)'}}/>
            </div>

          <form onSubmit={goToPhoto}>
            <label style={s.label}>Your full name</label>
            <input style={s.input} type="text" placeholder="e.g. Sarah Mitchell"
              value={fullName} onChange={e=>setFullName(e.target.value)} autoFocus/>
            {error && <div style={s.err}>{error}</div>}
            <button style={{...s.btn,...(!fullName.trim()?s.btnDis:{})}} disabled={!fullName.trim()}>
              Continue
            </button>
          </form>
          </>
        )}

        {step === 'photo' && (
          <>
            <div style={{...s.sub, marginTop:14, marginBottom:24}}>
              Add a profile photo so your church recognises you.
            </div>
            <div style={{display:'flex', justifyContent:'center', marginBottom:22}}>
              <button type="button" onClick={()=>photoInputRef.current?.click()} disabled={loading}
                style={{
                  width:140, height:140, borderRadius:'50%',
                  background: photoPreview ? `url(${photoPreview}) center/cover` : 'rgba(232,200,122,0.06)',
                  border: photoPreview ? `2px solid ${ACCENT}` : `2px dashed rgba(232,200,122,0.55)`,
                  cursor: loading ? 'default' : 'pointer',
                  display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                  color: 'var(--gold-ink)', padding:0,
                }}>
                {!photoPreview && (
                  <>
                    <div style={{fontSize:36, lineHeight:1, marginBottom:6}}>📷</div>
                    <div style={{fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', fontFamily:"'Plus Jakarta Sans',sans-serif"}}>Add photo</div>
                  </>
                )}
              </button>
              <input ref={photoInputRef} type="file" accept="image/*" style={{display:'none'}}
                onChange={e => pickPhoto(e.target.files?.[0])}/>
            </div>
            {error && <div style={s.err}>{error}</div>}
            {photoFile && (
              <button type="button" onClick={sendCode} disabled={loading}
                style={{...s.btn,...(loading?s.btnDis:{})}}>
                {loading ? 'Sending code…' : 'Continue'}
              </button>
            )}
            <div style={{textAlign:'center', marginTop: photoFile ? 14 : 12}}>
              <button type="button"
                onClick={photoFile ? () => photoInputRef.current?.click() : sendCode}
                disabled={loading}
                style={{
                  background:'transparent', border:'none', color:'var(--ink)',
                  fontSize:13, fontWeight:500, fontFamily:"'Plus Jakarta Sans',sans-serif",
                  textDecoration:'underline', textUnderlineOffset:3,
                  cursor: loading ? 'default' : 'pointer',
                  padding:'8px 4px', opacity: loading ? 0.5 : 1,
                }}>
                {loading && !photoFile ? 'Sending code…' : (photoFile ? 'Change photo' : 'Skip this for now')}
              </button>
            </div>
          </>
        )}

        {step === 'verify' && (
          <>
            <div style={s.msg}>
              ✉️ Almost there — we've emailed a {OTP_LENGTH}-digit code{email ? <> to <strong>{email}</strong></> : ''}. Enter it below to finish setting up.
            </div>
            <form autoComplete="one-time-code"
              onSubmit={e => { e.preventDefault(); if (!verifying) handleVerifyCode(code) }}>
              <OtpCodeInputs
                length={OTP_LENGTH}
                value={code}
                onChange={setCode}
                onSubmit={(full) => { if (!verifying) handleVerifyCode(full) }}
                disabled={verifying}
                autoFocus/>
            </form>
            {verifying && (
              <div style={{textAlign:'center', fontSize:12, color:'var(--stone)', marginTop:12}}>
                Verifying…
              </div>
            )}
            {error && <div style={s.err}>{error}</div>}
            {resentNote && (
              <div style={{...s.msg, marginTop:12, fontSize:12}}>{resentNote}</div>
            )}
            <div style={{textAlign:'center', fontSize:12, color:'var(--stone)', marginTop:16}}>
              Didn’t receive a code?{' '}
              <button type="button" onClick={handleResend} disabled={resending}
                style={{
                  background:'transparent', border:'none', color:'var(--gold-ink)',
                  cursor: resending ? 'default' : 'pointer', padding:0,
                  fontSize:12, fontWeight:700, fontFamily:"'Plus Jakarta Sans',sans-serif",
                  textDecoration:'underline', opacity: resending ? 0.5 : 1,
                }}>
                {resending ? 'Resending…' : 'Resend'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ── SUPER ADMIN SETUP - only accessible via /?setup=YOUR_SECRET
// Use this once to create your super admin account.
// After that, sign in normally via magic link.
export function SuperAdminSetup() {
  const { signInWithEmail } = useAuth()
  const [email,   setEmail]   = useState('')
  const [sent,    setSent]    = useState(false)
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState('')

  async function handleSubmit(e) {
    e.preventDefault()
    if (!email.trim()) return
    setLoading(true); setError('')

    // First create the user via magic link
    const { error: authErr } = await signInWithEmail(email.trim())
    if (authErr) { setError(authErr.message); setLoading(false); return }

    // The profile gets created by the database trigger.
    // After signing in via the magic link, manually set role to super_admin
    // in Supabase Table Editor → profiles → find your row → set role = super_admin
    setSent(true)
    setLoading(false)
  }

  return (
    <div style={s.wrap}>
      <div style={s.card}>
        <div style={s.logo}>Community Bible App</div>
        <div style={s.appName}>Super Admin Setup</div>
        <div style={s.sub}>
          This page creates your super admin account. Use it once, then sign in normally.
        </div>

        {sent ? (
          <div style={s.msg}>
            ✉️ Check your email for a sign-in link.<br/><br/>
            After signing in, go to:<br/>
            <strong>Supabase → Table Editor → profiles</strong><br/>
            Find your row and set <strong>role = super_admin</strong>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <label style={s.label}>Your email address</label>
            <input style={s.input} type="email" placeholder="you@example.com"
              value={email} onChange={e=>setEmail(e.target.value)} autoFocus/>
            {error && <div style={s.err}>{error}</div>}
            <button style={{...s.btn,...(loading||!email.trim()?s.btnDis:{})}}
              disabled={loading||!email.trim()}>
              {loading ? 'Sending…' : 'Create super admin account'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

// ── INVITE PANEL - shown inside the app for elders/admins
export function InvitePanel() {
  const { sendInvite, isElder, isAdmin, isSuperAdmin, profile, churches } = useAuth()
  const [contact,  setContact]  = useState('')
  const [role,     setRole]     = useState('member')
  const [churchId, setChurchId] = useState(isSuperAdmin ? '' : (profile?.church_id || ''))
  const [sent,     setSent]     = useState(null)
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState('')
  const [copied,   setCopied]   = useState(false)

  if (!isElder) return null

  const roles = [
    { id:'member', label:'Member' },
    { id:'elder',  label:'Elder',  hide:!isAdmin },
    { id:'admin',  label:'Admin',  hide:!isAdmin },
  ].filter(r => !r.hide)

  async function handleInvite(e) {
    e.preventDefault()
    if (!contact.trim()) return
    if (isSuperAdmin && !churchId) { setError('Please choose a church.'); return }
    setLoading(true); setError(''); setSent(null); setCopied(false)
    const targetChurchId = isSuperAdmin ? churchId : profile?.church_id
    const { data, error, message, inviteUrl, emailSent, emailError } = await sendInvite(contact.trim(), role, targetChurchId)
    setLoading(false)
    if (error) setError(error.message)
    else {
      setSent({
        ...data,
        _contact:    contact.trim(),
        _message:    message,
        _inviteUrl:  inviteUrl,
        _emailSent:  !!emailSent,
        _emailError: emailError || null,
      })
      setContact('')
    }
  }

  // Prefer the URL/message the hook built (canonical https://thebiblecommunity.co.uk
  // domain). Fall back to a window-origin URL for old code paths.
  const inviteLink     = sent ? (sent._inviteUrl || `${window.location.origin}/?invite=${sent.token}`) : ''
  const inviteMessage  = sent?._message || ''
  const inviteEmail    = sent?.email || (sent?._contact?.includes('@') ? sent._contact : '')
  const targetChurch   = sent ? churches.find(c => c.id === sent.church_id) : null
  const churchName     = targetChurch?.name || 'our church'

  async function copyMessage() {
    try {
      await navigator.clipboard.writeText(inviteMessage || inviteLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      window.prompt('Copy this invite message:', inviteMessage || inviteLink)
    }
  }

  function mailtoLink() {
    const subject = encodeURIComponent(`You're invited to join ${churchName}`)
    const body    = encodeURIComponent(
`Hi,

I'd like to invite you to join ${churchName} on the Community Bible App.

Click this link to set up your account:
${inviteLink}

The link expires in 7 days.

- Sent via Community Bible App`
    )
    return `mailto:${inviteEmail}?subject=${subject}&body=${body}`
  }

  return (
    <div style={{padding:'16px'}}>
      <div style={{fontSize:10,fontWeight:700,letterSpacing:'0.15em',textTransform:'uppercase',color:'var(--stone)',marginBottom:12}}>
        Invite someone
      </div>
      <form onSubmit={handleInvite}>
        {isSuperAdmin && (
          <>
            <label style={{...s.label,marginTop:0}}>Church</label>
            <select style={{...s.input,marginBottom:10,appearance:'none'}}
              value={churchId} onChange={e=>setChurchId(e.target.value)}>
              <option value="">Select a church…</option>
              {churches.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </>
        )}
        <div style={s.roleRow}>
          {roles.map(r => (
            <div key={r.id} style={{...s.roleBt,...(role===r.id?s.roleOn:{})}} onClick={()=>setRole(r.id)}>{r.label}</div>
          ))}
        </div>
        <input style={{...s.input,marginBottom:10}} type="text"
          placeholder="Email address or phone number"
          value={contact} onChange={e=>setContact(e.target.value)}/>
        {error && <div style={s.err}>{error}</div>}
        {sent && (
          <div style={{...s.msg, lineHeight:1.5}}>
            <div style={{fontWeight:700, marginBottom:4}}>
              {sent._emailSent ? 'Email invite sent!' : `✓ Invite created for ${sent._contact}`}
            </div>
            <div style={{fontSize:12, opacity:0.85, marginBottom:10}}>
              {sent._emailSent
                ? 'You can also share their invite link here:'
                : (sent._emailError
                    ? `Couldn't send the email automatically (${sent._emailError}). Share the link below instead.`
                    : 'Share the link below via WhatsApp, text or email. The link expires in 7 days.')}
            </div>
            <div style={{
              fontSize:12, lineHeight:1.5,
              background:'var(--cream)',
              border:'1px solid var(--border-strong)',
              borderRadius:8, padding:'10px 12px',
              whiteSpace:'pre-wrap', wordBreak:'break-word',
              userSelect:'all',
              marginBottom:10,
              color:'var(--ink)',
            }}>
              {inviteMessage || inviteLink}
            </div>
            <div style={{display:'flex', gap:8}}>
              <button type="button" onClick={copyMessage}
                style={{flex:1, background:ACCENT, color:NAVY, border:'none', borderRadius:8, padding:'10px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                {copied ? '✓ Copied' : '📋 Copy message'}
              </button>
              {inviteEmail && (
                <a href={mailtoLink()}
                  style={{flex:1, background:'var(--white)', color:'var(--ink)', border:'1px solid var(--border-strong)', borderRadius:8, padding:'10px', fontSize:13, fontWeight:700, textAlign:'center', textDecoration:'none', fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
                  ✉️ Email it
                </a>
              )}
            </div>
          </div>
        )}
        <button style={{...s.btn,marginTop:8,...(loading||(isSuperAdmin&&!churchId)?s.btnDis:{})}}
          disabled={loading||!contact.trim()||(isSuperAdmin&&!churchId)}>
          {loading ? 'Creating…' : 'Create invite'}
        </button>
      </form>
    </div>
  )
}

// ── CREATE CHURCH PANEL - super admin only
export function CreateChurchPanel() {
  const { createChurch, isSuperAdmin } = useAuth()
  const [name,       setName]       = useState('')
  const [slug,       setSlug]       = useState('')
  const [location,   setLocation]   = useState('')
  const [website,    setWebsite]    = useState('')
  const [adminEmail, setAdminEmail] = useState('')
  const [done,       setDone]       = useState(null)
  const [loading,    setLoading]    = useState(false)
  const [error,      setError]      = useState('')

  if (!isSuperAdmin) return null

  function toSlug(val) {
    return val.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'')
  }

  async function handleCreate(e) {
    e.preventDefault()
    if (!name) return
    setLoading(true); setError('')
    const { church, invite, error } = await createChurch(name, location, website, adminEmail)
    setLoading(false)
    if (error) setError(error.message)
    else setDone({ church, invite })
  }

  return (
    <div style={{padding:'16px'}}>
      <div style={{fontSize:10,fontWeight:700,letterSpacing:'0.15em',textTransform:'uppercase',color:'var(--gold-ink)',marginBottom:16}}>
        ✦ Add new church
      </div>
      {done ? (
        <div style={s.msg}>
          ✓ <strong>{done.church.name}</strong> created.<br/>
          Admin invite sent to {adminEmail}.<br/>
          <div style={{marginTop:8,fontSize:11,opacity:0.7}}>
            Invite link: {window.location.origin}/?invite={done.invite?.token}
          </div>
        </div>
      ) : (
        <form onSubmit={handleCreate}>
          <label style={s.label}>Church name *</label>
          <input style={s.input} placeholder="e.g. St John's Church – Bristol"
            value={name} onChange={e=>{setName(e.target.value);setSlug(toSlug(e.target.value))}}/>
          <label style={s.label}>URL slug *</label>
          <input style={s.input} placeholder="e.g. st-johns-bristol"
            value={slug} onChange={e=>setSlug(toSlug(e.target.value))}/>
          <label style={s.label}>Location</label>
          <input style={s.input} placeholder="e.g. Bristol, UK"
            value={location} onChange={e=>setLocation(e.target.value)}/>
          <label style={s.label}>Website</label>
          <input style={s.input} type="url" placeholder="https://..."
            value={website} onChange={e=>setWebsite(e.target.value)}/>
          <label style={s.label}>First admin's email *</label>
          <input style={s.input} type="email" placeholder="pastor@church.org"
            value={adminEmail} onChange={e=>setAdminEmail(e.target.value)}/>
          {error && <div style={s.err}>{error}</div>}
          <button style={{...s.btn,marginTop:12,...(loading?s.btnDis:{})}}
            disabled={loading||!name||!slug||!adminEmail}>
            {loading ? 'Creating…' : 'Create church + send admin invite'}
          </button>
        </form>
      )}
    </div>
  )
}
